/**
 * Mention rendering helper (Milestone 4-B, spec §3.9).
 *
 * Parses inline @mention syntax used in card notes, sub-task bodies,
 * comments and role-message bodies. Patterns mirror the parser on the
 * backend (apps/chaos-gateway/src/mentions-parser.ts):
 *
 *   @user:<slug-or-uuid>                  → user mention pill
 *   @role:<slug>                          → role mention pill
 *   @artifact:current/<path>              → artifact ref pill (current card)
 *   @artifact:card:<uuid>/<path>          → artifact ref pill (cross-card)
 *   @artifact:url:<url>                   → external URL ref pill
 *   @<simple-name>                        → soft user mention (slug)
 *
 * The renderer is **read-only** — it doesn't transform user input on
 * submit, only the displayed version. Pills carry hover tooltips with
 * the raw token for fast copy-paste / debugging.
 */
import * as React from 'react';
import { AtSign, Briefcase, Paperclip, Link as LinkIcon } from 'lucide-react';

import { cn } from './utils';

/**
 * Master regex — `g` flag for split iteration.
 *
 *   1. @artifact:current/<path>
 *   2. @artifact:card:<uuid>/<path>
 *   3. @artifact:url:<full-url>
 *   4. @user:<slug-or-uuid>
 *   5. @role:<slug>
 *   6. @<bare-name>     — fallback soft user mention
 *
 * Order matters: most specific first.
 */
const MENTION_RE =
  /@artifact:url:(\S+)|@artifact:card:([0-9a-f-]{36})\/(\S+)|@artifact:current\/(\S+)|@user:([A-Za-z0-9._-]+|[0-9a-f-]{36})|@role:([A-Za-z0-9._-]+)|@([A-Za-z][A-Za-z0-9._-]{1,40})/g;

export interface ParsedMention {
  kind: 'user' | 'role' | 'artifact';
  raw: string;
  /** For users — slug or uuid; for roles — slug; for artifacts — path/url. */
  target: string;
  /** For artifact:card — the referenced card id. */
  cardId?: string;
  /** For artifact:url — the url. */
  url?: string;
  /** For artifact:current — implicit (uses current card context). */
  scope?: 'current' | 'card' | 'url';
}

type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; data: ParsedMention };

/**
 * Tokenises a markdown body into plain-text + mention segments. Caller
 * is free to render whatever it wants for each kind; the default
 * <MentionText> renders inline pills + verbatim text.
 */
export function tokenize(body: string): Segment[] {
  const out: Segment[] = [];
  if (!body) return out;
  let lastIndex = 0;
  MENTION_RE.lastIndex = 0;
  for (let m: RegExpExecArray | null; (m = MENTION_RE.exec(body)) !== null; ) {
    const [raw, artUrl, artCardId, artCardPath, artCurPath, userTok, roleSlug, bareUser] = m;
    if (m.index > lastIndex) {
      out.push({ kind: 'text', text: body.slice(lastIndex, m.index) });
    }
    if (artUrl !== undefined) {
      out.push({
        kind: 'mention',
        data: { kind: 'artifact', raw, target: artUrl, scope: 'url', url: artUrl },
      });
    } else if (artCardId !== undefined) {
      out.push({
        kind: 'mention',
        data: {
          kind: 'artifact',
          raw,
          target: artCardPath ?? '',
          scope: 'card',
          cardId: artCardId,
        },
      });
    } else if (artCurPath !== undefined) {
      out.push({
        kind: 'mention',
        data: { kind: 'artifact', raw, target: artCurPath, scope: 'current' },
      });
    } else if (userTok !== undefined) {
      out.push({
        kind: 'mention',
        data: { kind: 'user', raw, target: userTok },
      });
    } else if (roleSlug !== undefined) {
      out.push({
        kind: 'mention',
        data: { kind: 'role', raw, target: roleSlug },
      });
    } else if (bareUser !== undefined) {
      out.push({
        kind: 'mention',
        data: { kind: 'user', raw, target: bareUser },
      });
    }
    lastIndex = m.index + raw.length;
  }
  if (lastIndex < body.length) {
    out.push({ kind: 'text', text: body.slice(lastIndex) });
  }
  return out;
}

interface MentionPillProps {
  data: ParsedMention;
  className?: string;
}

export function MentionPill({ data, className }: MentionPillProps) {
  const baseCls =
    'inline-flex items-center gap-1 rounded-sm bg-foreground/5 px-1 py-px text-[12px] font-medium text-foreground transition-colors hover:bg-foreground/10';
  const title = data.raw;
  if (data.kind === 'user') {
    return (
      <span className={cn(baseCls, className)} title={title} data-mention-kind="user">
        <AtSign className="h-3 w-3" aria-hidden />
        {data.target}
      </span>
    );
  }
  if (data.kind === 'role') {
    return (
      <span className={cn(baseCls, className)} title={title} data-mention-kind="role">
        <Briefcase className="h-3 w-3" aria-hidden />
        {data.target}
      </span>
    );
  }
  // Artifact
  if (data.scope === 'url' && data.url) {
    return (
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(baseCls, 'underline-offset-2 hover:underline', className)}
        title={title}
        data-mention-kind="artifact-url"
      >
        <LinkIcon className="h-3 w-3" aria-hidden />
        {trimUrl(data.url)}
      </a>
    );
  }
  return (
    <span className={cn(baseCls, className)} title={title} data-mention-kind="artifact">
      <Paperclip className="h-3 w-3" aria-hidden />
      {data.target}
    </span>
  );
}

function trimUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname.length > 1 ? u.pathname : ''}`.slice(0, 40);
  } catch {
    return url.slice(0, 40);
  }
}

interface MentionTextProps {
  body: string;
  className?: string;
  /**
   * When true, mention pills are rendered as plain text (for previews
   * etc). Defaults to false. Useful for compact contexts like the
   * mentions inbox where pills should not visually compete.
   */
  flatten?: boolean;
}

/**
 * Render markdown body with @mentions resolved to pills. Whitespace +
 * newlines are preserved (`<span>` with `whitespace-pre-wrap`).
 */
export function MentionText({ body, className, flatten }: MentionTextProps) {
  const segments = React.useMemo(() => tokenize(body), [body]);
  return (
    <span className={cn('whitespace-pre-wrap break-words', className)}>
      {segments.map((s, i) =>
        s.kind === 'text' ? (
          <React.Fragment key={i}>{s.text}</React.Fragment>
        ) : flatten ? (
          <span key={i} className="font-medium text-foreground/80">
            {s.data.raw}
          </span>
        ) : (
          <MentionPill key={i} data={s.data} />
        ),
      )}
    </span>
  );
}

/**
 * Strip context snippet for the mentions inbox preview. Centres the
 * highlighted mention; expects `contextBefore`/`contextAfter` from the
 * mention row. The mention itself is recovered from the raw token of
 * the body if available, otherwise from contextBefore tail.
 */
export interface ContextPreviewProps {
  before: string | null;
  after: string | null;
  /** Raw mention token to highlight at the join, e.g. `@founder`. */
  highlight?: string | null;
  className?: string;
}

export function ContextPreview({ before, after, highlight, className }: ContextPreviewProps) {
  return (
    <span className={cn('text-[12px] leading-snug text-muted-foreground', className)}>
      {before ? <span>…{trimEdge(before, 80, 'tail')}</span> : null}
      {highlight ? (
        <span className="mx-1 rounded-sm bg-foreground/10 px-1 font-medium text-foreground">
          {highlight}
        </span>
      ) : null}
      {after ? <span>{trimEdge(after, 80, 'head')}… </span> : null}
    </span>
  );
}

function trimEdge(s: string, max: number, side: 'head' | 'tail'): string {
  if (s.length <= max) return s;
  return side === 'tail' ? s.slice(s.length - max) : s.slice(0, max);
}
